import { ReportCardLevel } from '../../components/Report/SchemaReportCard/schema-types';

export interface PlayerReportCardLevelData {
  playerAssessmentId: string;
  reportCardLevel: ReportCardLevel;
  fetchedAt: Date;
}

/**
 * Service for managing report card levels
 * Report card levels are now fetched from GraphQL/BigQuery on the server side
 */
export class ReportCardLevelService {
  private static instance: ReportCardLevelService;

  private constructor() {}

  public static getInstance(): ReportCardLevelService {
    if (!ReportCardLevelService.instance) {
      ReportCardLevelService.instance = new ReportCardLevelService();
    }
    return ReportCardLevelService.instance;
  }

  /**
   * Gets report card level from player assessment data
   * @param playerAssessment - The player assessment object with reportCardLevel
   * @returns The report card level
   */
  public getReportCardLevel(playerAssessment: IPlayerAssessment): ReportCardLevel {
    // Use the reportCardLevel field from the GraphQL response
    const level = playerAssessment.reportCardLevel;

    // Convert string to enum
    switch (level) {
      case 'BRONZE':
        return ReportCardLevel.BRONZE;
      case 'SILVER':
        return ReportCardLevel.SILVER;
      case 'GOLD':
        return ReportCardLevel.GOLD;
      default:
        return ReportCardLevel.BRONZE;
    }
  }

  /**
   * Checks if a player has access to a specific report card level
   * @param playerLevel - The player's current report card level
   * @param requiredLevel - The level required to access a component
   * @returns boolean indicating if the player has access
   */
  public static hasAccessToLevel(playerLevel: ReportCardLevel, requiredLevel: ReportCardLevel): boolean {
    const levelHierarchy = {
      [ReportCardLevel.BRONZE]: 1,
      [ReportCardLevel.SILVER]: 2,
      [ReportCardLevel.GOLD]: 3
    };

    return levelHierarchy[playerLevel] >= levelHierarchy[requiredLevel];
  }

  /**
   * Determines if a component should be shown based on supported levels and player level
   * @param supportedLevels - Array of levels that support this component (undefined means all levels)
   * @param playerLevel - The player's current report card level
   * @returns boolean indicating if the component should be shown
   */
  public static shouldShowComponent(supportedLevels: ReportCardLevel[] | undefined, playerLevel: ReportCardLevel): boolean {
    // If no levels specified, component is available for all levels
    if (!supportedLevels || supportedLevels.length === 0) {
      return true;
    }

    // Check if player's level is in the supported levels array
    return supportedLevels.includes(playerLevel);
  }
}

// Export singleton instance
export const reportCardLevelService = ReportCardLevelService.getInstance();
